// Using Vue i18n for internationalization
// https://vue-i18n.intlify.dev
// https://github.com/intlify/vue-i18n

import { createI18n } from 'vue-i18n';

import componentEn from '../../locales/en.json'; // EN strings
import componentFr from '../../locales/fr.json'; // FR strings
import common from '../../locales/common.json'; // Common strings

import { Locales } from './useConstants';

const DEFAULT_LOCALE = Locales[0];
const SUPPORTED_LOCALES = Locales;

const PLACEHOLDER_PREFIX = '||';

let i18n;

export function useI18n() {
    const initializeI18n = (appLocales) => {
        // Merge the app translation strings with the component library strings
        const en = { ...appLocales.en, ...componentEn };
        const fr = { ...appLocales.fr, ...componentFr };
        const messages = { en, fr };

        replacePlaceholders(messages);

        i18n = createI18n({
            legacy: false,
            locale: DEFAULT_LOCALE,
            fallbackLocale: DEFAULT_LOCALE,
            messages: messages,
            datetimeFormats: getDateTimeFormats(),
        });
        return i18n;
    };

    const usingDefaultLocale = () => {
        const locale = i18n?.global?.locale?.value;
        return !locale || locale === DEFAULT_LOCALE;
    };

    const processRouteLocale = async (to, _from, next) => {
        console.log(`processRouteLocale to=${to?.fullPath}, _from=${_from?.fullPath}`);
        const locale = SUPPORTED_LOCALES.includes(to.params.locale) ? to.params.locale : DEFAULT_LOCALE;
        console.log(`Locale set to ${locale}`);
        i18n.global.locale.value = locale;
        if (to.params.locale && locale === DEFAULT_LOCALE) {
            delete to.params.locale;
            return next(to);
        }
        return next();
    };

    const pathWithLocale = (path) => {
        const locale = i18n?.global?.locale?.value;
        return !locale || locale === DEFAULT_LOCALE || !SUPPORTED_LOCALES.includes(locale) ? path : `${locale}/${path}`;
    };

    // Replace placeholders in the format ||common.key|| with the string from common.json. e.g. ||product.name||
    // This allows us to store all product-related strings in common.json,
    // separate from the locale strings provided to translators.
    const replacePlaceholders = (messages) => {
        try {
            for (const locale in messages) {
                let str = JSON.stringify(messages[locale]);
                let placeholder = getNextPlaceholder(str);
                while (placeholder) {
                    try {
                        const value = getPlaceholderValue(placeholder);
                        console.log(`replacePlaceholders (${locale}): set ${placeholder} to '${value}'`);
                        str = str.replaceAll(placeholder, value);
                    } catch (error) {
                        console.error(`replacePlaceholders (${locale}): placeholder ${placeholder}`, error);
                        str = str.replaceAll(placeholder, '');
                    }
                    placeholder = getNextPlaceholder(str);
                }
                messages[locale] = { ...JSON.parse(str), ...common };
            }
        } catch (error) {
            console.error(`replacePlaceholders`, error);
        }
    };

    const getNextPlaceholder = (str) => {
        const start = str.indexOf(PLACEHOLDER_PREFIX);
        if (start >= 0) {
            const end = str.indexOf(PLACEHOLDER_PREFIX, start + PLACEHOLDER_PREFIX.length);
            if (end > 0) {
                return str.substring(start, end + PLACEHOLDER_PREFIX.length).trim();
            }
        }
        return null;
    };

    const getPlaceholderValue = (placeholder) => {
        let value = common;
        const keys = placeholder.substring(PLACEHOLDER_PREFIX.length, placeholder.length - PLACEHOLDER_PREFIX.length).split('.');
        if (keys.length == 0) {
            throw new Error(`Invalid placeholder - must be set to a key in common.json`);
        }
        for (const key of keys) {
            value = value[key];
        }
        if (!value) {
            throw new Error(`Placeholder not found in common.json`);
        }
        return value;
    };

    const getDateTimeFormats = () => {
        // https://vue-i18n.intlify.dev/guide/essentials/datetime.html
        // Vue i18n's date parser requires the options in ECMA-402 Intl.DateTimeFormat: https://tc39.es/ecma402/#datetimeformat-objects
        // There is no way to specify a custom format/delimitor. See https://github.com/tc39/ecma402/issues/554
        // If this is too limiting, alternatively we could create our own date parser instead of using Vue i18n.
        return {
            en: {
                // short: MMM DD, YYYY
                short: {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                },
                // long: MMM DD, YYYY, hh:mm
                long: {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true,
                },
                time: {
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true,
                },
                // monthDay: MMM DD
                monthDay: {
                    month: 'short',
                    day: 'numeric',
                },
                // year: YYYY
                year: {
                    year: 'numeric',
                },
            },
            fr: {
                // short: MMM DD, YYYY
                short: {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                },
                // long: MMM DD, YYYY, hh:mm
                long: {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: false,
                },
                time: {
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: false,
                },
                // monthDay: MMM DD
                monthDay: {
                    month: 'short',
                    day: 'numeric',
                },
                // year: YYYY
                year: {
                    year: 'numeric',
                },
            },
        };
    };

    /**
     * Intercepts and formats date strings to align with Canadian English style standards.
     *
     * This helper targets standard 3-letter English month abbreviations generated by
     * Intl.DateTimeFormat (via Vue i18n). It appends a period to all abbreviated months
     * (e.g., "Jan" -> "Jan.") and specifically converts "Sep" to "Sept.".
     * It leaves "May" untouched since it is not an abbreviation.
     *
     * If the active locale is French, the original string is returned without modification to preserve native grammatical formatting rules.
     *
     * @param {string} dateString - The raw, localized date string generated by Vue i18n.
     * @returns {string} The formatted date string with proper month abbreviations for English,
     *                   or the untouched original string for other locales.
     */
    const applyEnglishMonthOverrides = (dateString) => {
        const currentLocale = i18n.global.locale.value || i18n.global.locale;
        if (!currentLocale.startsWith('en')) {
            return dateString;
        }
        return dateString.replace(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/, (match) => {
            if (match === 'Sep') return 'Sept.';
            if (match === 'May') return 'May';
            return `${match}.`;
        });
    };

    const getLongDateFromTimestamp = (epochMs) => {
        if (!epochMs) return '';
        const rawDate = i18n.global.d(Number(epochMs), 'long');
        return applyEnglishMonthOverrides(rawDate);
    };

    const getShortDateFromTimestamp = (epochMs) => {
        if (!epochMs) return '';
        const rawDate = i18n.global.d(Number(epochMs), 'short');
        return applyEnglishMonthOverrides(rawDate);
    };

    const getTimeFromTimestamp = (epochMs) => {
        return epochMs ? i18n.global.d(Number(epochMs), 'time') : '';
    };

    /**
     * Formats a timestamp as a time only if it falls on today's (local) date, otherwise as a long date and time.
     * @param {(string|number)} epochMs - the epoch timestamp in milliseconds.
     * @returns {string} e.g. '3:45 PM' (today) or 'Sept. 23, 2026, 3:45 PM' (other days), or '' if the timestamp is invalid.
     */
    const getTimeOrLongDateFromTimestamp = (epochMs) => {
        if (!epochMs) return '';
        const date = new Date(Number(epochMs));
        if (isNaN(date.getTime())) return '';
        const now = new Date();
        const isToday = date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
        return isToday ? getTimeFromTimestamp(epochMs) : getLongDateFromTimestamp(epochMs);
    };

    /**
     * Formats a timestamp as a short month and day if it falls in the current (local) year, otherwise as the year only.
     * @param {(string|number)} epochMs - the epoch timestamp in milliseconds.
     * @returns {string} e.g. 'Mar. 24' (current year) or '2025' (other years), or '' if the timestamp is invalid.
     */
    const getMonthDayOrYearFromTimestamp = (epochMs) => {
        if (!epochMs) return '';
        const date = new Date(Number(epochMs));
        if (isNaN(date.getTime())) return '';
        const isCurrentYear = date.getFullYear() === new Date().getFullYear();
        return isCurrentYear ? applyEnglishMonthOverrides(i18n.global.d(date, 'monthDay')) : i18n.global.d(date, 'year');
    };

    const getTimeStampFromSeconds = (epochSeconds) => {
        if (!epochSeconds) {
            return '';
        }

        const epochMs = Number(epochSeconds) * 1000;
        return i18n.global.d(epochMs, 'short');
    };

    const getMessagesWithPrefix = (pathPrefix) => {
        const locale = i18n?.global?.locale?.value;
        const messages = locale ? i18n.global.messages.value[locale] : {};
        const parts = pathPrefix.split('.');
        return parts.reduce((current, key) => {
            return current && typeof current === 'object' ? current[key] : undefined;
        }, messages);
    };

    /**
     * Formats a phone number string or number into the North American format (XXX) XXX-XXXX.
     * In the future we could extend this to support formats for other regions.
     * @param {string|number} phoneNumber
     * @returns string the formatted phone number or an empty string the original input is not valid
     */
    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) {
            return '';
        }
        // Format the phone number as (XXX) XXX-XXXX
        const cleaned = ('' + phoneNumber).replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (match) {
            return `(${match[1]}) ${match[2]}-${match[3]}`;
        }
        return phoneNumber;
    };

    /**
     * Formats a property name with the appropriate localized separator.
     * @param {string} name - The translated name of the property.
     * @returns {string} - The formatted label (e.g., "Nom :" in FR, "Name:" in EN).
     */
    const formatLabelWithColon = (name) => {
        return i18n.global.t('common.properties.name', { name });
    };

    /**
     * Retrieves a translated property value from an object based on the current Vue i18n locale.
     * * This function attempts to find a property appended with the current locale suffix
     * (e.g., `description_fr`). If the localized property does not exist it falls back to the base `propertyName`.
     *
     * @param {Record<string, any>} properties - The data object containing the translated and base properties.
     * @param {string} propertyName - The base name of the property to retrieve (e.g., 'title').
     * @returns {*} The value of the translated property if defined; otherwise, the base property value.
     */
    const getTranslatedProperty = (properties, propertyName) => {
        const locale = i18n?.global?.locale?.value;
        const translatedValue = properties[`${propertyName}_${locale}`];
        return translatedValue !== undefined ? translatedValue : properties[propertyName];
    };

    return {
        initializeI18n,
        processRouteLocale,
        usingDefaultLocale,
        pathWithLocale,
        getLongDateFromTimestamp,
        getShortDateFromTimestamp,
        getTimeFromTimestamp,
        getTimeOrLongDateFromTimestamp,
        getMonthDayOrYearFromTimestamp,
        getMessagesWithPrefix,
        getTimeStampFromSeconds,
        getTranslatedProperty,
        formatPhoneNumber,
        formatLabelWithColon,
    };
}
